/**
 * @graphql cc
 */
import React, { useContext, useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead

import { Subscribe } from 'unstated';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';

import { PageLoadEnd } from '@confluence/browser-metrics';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError as is403 } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { isUnauthorizedError as is401 } from '@confluence/error-types';
import {
	RESTRICTED_PAGE_EXPERIENCE,
	ExperienceStart,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import type { WithFlagsProps } from '@confluence/flags';
import { FlagsStateContainer } from '@confluence/flags';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { CONTEXT_PATH, VIEW_PAGE } from '@confluence/named-routes';
import { ErrorPageComponent } from '@confluence/not-found';
import { useContentType } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useFeatureCodesInURL } from '@confluence/feature-codes';
import { useSpaceKey } from '@confluence/space-utils';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';

import { INACCESSIBLE_PAGE_LOAD } from './perf.config';
import RestrictedContentIconLight from './RestrictedContentIcon-Light.svg';
import RestrictedContentIconDark from './RestrictedContentIcon-Dark.svg';
import { SuperAdminGateway } from './SuperAdminGateway';
import type { RequestViewQuery as RequestViewQueryType } from './__types__/RequestViewQuery';
import { RequestViewQuery } from './RequestViewQuery.graphql';
import type {
	restrictedPageRequestPageAccessMutation,
	restrictedPageRequestPageAccessMutationVariables,
} from './__types__/restrictedPageRequestPageAccessMutation';
import { AccessType } from './__types__/restrictedPageRequestPageAccessMutation';
import { ShowSpinnerAfterDelay } from './ShowSpinnerAfterDelay';

const RestrictedPageRequestPageAccessMutation = gql`
	mutation RestrictedPageRequestPageAccessMutation(
		$requestPageAccessInput: RequestPageAccessInput!
	) {
		requestPageAccess(requestPageAccessInput: $requestPageAccessInput) {
			displayName
		}
	}
`;

const SUCCESS_FLAG_ID = 'reqestAccessSuccess';

type RestrictedPageProps = {
	accessType: 'inherited' | 'view' | 'edit';
	contentId: string | undefined | null;
	showSuperAdminGateway?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div<{ isMobile: boolean }>({
	marginTop: token('space.200'),
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isMobile }) => (isMobile ? 'center' : 'left'),
	gap: token('space.050'),
});

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'RESTRICTED_PAGE',
};

const i18n = defineMessages({
	requestSentTitle: {
		id: 'no-permission.restricted.page.request.access.successful.title.2',
		defaultMessage: 'Request sent',
		description: 'Title of notification after request successfully sent',
	},
	requestSentBody: {
		id: 'no-permission.restricted.page.request.access.successful.message',
		defaultMessage:
			'We’ve asked {ownerName} to give you access. \nYou’ll get an email once the request is approved',
		description: 'Message body of notification after request successfully sent',
	},
	requestFailedTitle: {
		id: 'no-permission.restricted.page.request.access.failed.title',
		defaultMessage: 'Access request unsuccessful',
		description: 'Title of notification after request failed',
	},
	requestFailedBody: {
		id: 'no-permission.restricted.page.request.access.failed.message.2',
		defaultMessage:
			'We ran into a problem submitting your access request. Try again or contact your admin.',
		description: 'Message body of notification after request failed',
	},
});

const RestrictedPageComponent = ({
	accessType,
	contentId,
	editDeniedButCanView,
	flags,
	viewLink,
	showSuperAdminGateway = true,
}: RestrictedPageProps &
	WithFlagsProps & {
		editDeniedButCanView: boolean;
		viewLink: string | null;
	}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentType] = useContentType();
	const [requestSent, setRequestSent] = useState(false);

	const { from } = useFeatureCodesInURL();

	useEffect(() => {
		// Yes, it's always a 'page' viewed, even if contentType is something
		// else. It's just how this screen is registered.
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'restrictedPageViewed',
				action: 'viewed',
				actionSubject: 'page',
				actionSubjectId: 'restrictedPage',
				attributes: {
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
					editDeniedButCanView,
					from,
				},
			},
		}).fire();
	}, [
		accessType,
		contentId,
		contentType,
		editDeniedButCanView,
		viewLink,
		createAnalyticsEvent,
		from,
	]);

	const makeLinkClickedHandler = (buttonLocation: string) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewPageButton',
				source: 'restrictedPageScreen',
				attributes: {
					buttonLocation,
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
				},
			},
		}).fire();

		// If the link was in a flag, hide the flag, since this might be an
		// SPA transition.
		void flags.hideFlag(SUCCESS_FLAG_ID);
	};

	/** Displays the success or failure flags after access is requested */
	const handleRequestComplete = (result: restrictedPageRequestPageAccessMutation) => {
		const requestSuccessful = result?.requestPageAccess?.displayName;

		if (requestSuccessful) {
			const ownerName = result?.requestPageAccess?.displayName;

			void flags.showSuccessFlag({
				id: SUCCESS_FLAG_ID,
				title: formatMessage(i18n.requestSentTitle),
				description: formatMessage(i18n.requestSentBody, { ownerName }),
				actions: editDeniedButCanView
					? [
							{
								content: (
									<FormattedMessage
										id="no-permission.restricted.page.flag.view-content"
										description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
										defaultMessage="View the content now"
									/>
								),
								onClick: makeLinkClickedHandler('access-requested-flag'),
								href: viewLink!,
							},
						]
					: undefined,
			});
		} else {
			void flags.showErrorFlag({
				title: formatMessage(i18n.requestFailedTitle),
				description: formatMessage(i18n.requestFailedBody),
			});
		}

		setRequestSent(true);
	};

	const handleRequestError = () => {
		void flags.showErrorFlag({
			title: formatMessage(i18n.requestFailedTitle),
			description: formatMessage(i18n.requestFailedBody),
		});
		setRequestSent(true);
	};

	const [requestAccess] = useMutation<
		restrictedPageRequestPageAccessMutation,
		restrictedPageRequestPageAccessMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		RestrictedPageRequestPageAccessMutation,
		{
			variables: {
				requestPageAccessInput: {
					pageId: contentId ?? '',
					accessType: accessType === 'edit' ? AccessType.EDIT : AccessType.VIEW,
				},
			},
			onCompleted: handleRequestComplete,
			onError: handleRequestError,
		},
	);

	const renderTitle = () =>
		accessType === 'edit' ? (
			editDeniedButCanView ? (
				<FormattedMessage
					id="no-permission.restricted.page.edit-but-can-view.title"
					description="Title displayed for a page which the user cannot edit but could view"
					defaultMessage="You don’t have access to edit this content"
				/>
			) : (
				<FormattedMessage
					id="no-permission.restricted.page.edit.title"
					description="Title displayed for a page that has editing restricted"
					defaultMessage="Editing is restricted for this content"
				/>
			)
		) : (
			<FormattedMessage
				id="no-permission.restricted.page.view.title"
				description="Title displayed for a page that has viewing restricted"
				defaultMessage="You’ve stumbled on restricted content"
			/>
		);

	const renderMessage = () =>
		accessType === 'inherited' ? (
			<FormattedMessage
				id="no-permission.restricted.page.inherited.message"
				description="Message displayed when a page/content restriction is inherited from parent"
				defaultMessage="This is because it’s inheriting restrictions from a parent item. A space admin or the person who shared this content may be able to give you access."
			/>
		) : (
			renderAccessRequest()
		);

	/** Renders the access request call to action and message */
	const renderAccessRequest = () =>
		contentId ? (
			requestSent ? null : (
				<>
					{accessType === 'edit' ? (
						editDeniedButCanView ? (
							<FormattedMessage
								id="no-permission.restricted.page.edit.body.cannot-edit-can-view"
								description="Link to request editing access to a restricted page"
								defaultMessage="This could be because you don’t have permission to edit this content specifically or because a space admin hasn’t given you permission to edit content in this space."
							/>
						) : (
							<FormattedMessage
								id="no-permission.restricted.page.edit.request.access.from.author"
								description="Link to request editing access to a restricted page"
								defaultMessage="Only some people can edit this.
                If you think you should be one of them, request access from the author."
							/>
						)
					) : (
						<FormattedMessage
							id="no-permission.restricted.page.view.request.access.from.author"
							description="Link to request viewing access to a restricted page"
							defaultMessage="Only some people can view this.
                If you think you should be one of them, request access from the author."
						/>
					)}
					<ButtonContainer isMobile={shouldShowMobileWeb()}>
						{editDeniedButCanView && (
							<Button href={viewLink!} onClick={makeLinkClickedHandler('main')}>
								<FormattedMessage
									id="no-permission.restricted.page.view-content"
									description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
									defaultMessage="View content"
								/>
							</Button>
						)}
						<Button
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'requestAccessButton',
										source: 'restrictedPageScreen',
										attributes: {
											accessType: accessType || 'unspecified',
											contentId,
											contentType,
											editDeniedButCanView,
										},
									},
								}).fire();

								void requestAccess();
							}}
							appearance="primary"
						>
							{editDeniedButCanView ? (
								<FormattedMessage
									id="no-permission.restricted.page.request.to-edit"
									description="Button label for requesting access to edit a page"
									defaultMessage="Request to edit"
								/>
							) : (
								<FormattedMessage
									id="no-permission.restricted.page.request.access"
									description="Button label for requesting access to a page"
									defaultMessage="Request access"
								/>
							)}
						</Button>
					</ButtonContainer>
				</>
			)
		) : (
			<FormattedMessage
				id="no-permission.restricted.page.manual.request.access.from.author"
				description="Instructions to manually request access to a restricted page from the page's author"
				defaultMessage="Only some people can view this. If you think you should have access, try asking the person who sent you here for help."
			/>
		);

	return (
		<>
			<ErrorPageComponent
				errorImage={RestrictedContentIconLight}
				errorImageDark={RestrictedContentIconDark}
				errorTitle={renderTitle()}
				errorMessage={renderMessage()}
			>
				{showSuperAdminGateway && <SuperAdminGateway type="page" contentId={contentId} />}
			</ErrorPageComponent>
			<ExperienceSuccess name={RESTRICTED_PAGE_EXPERIENCE} />
			<PageLoadEnd
				metric={INACCESSIBLE_PAGE_LOAD}
				customData={{ ...CUSTOM_ATTRIBUTES, contentId }}
			/>
		</>
	);
};

const RestrictedPageWithCanViewQuery = (props: RestrictedPageProps & WithFlagsProps) => {
	const { accessType, contentId } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const skipViewQuery = accessType !== 'edit';
	const spaceKey = useSpaceKey();
	const { replace } = useRouteActions();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { data, error, loading } = useQuery<RequestViewQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		RequestViewQuery,
		{
			variables: { id: contentId },
			skip: skipViewQuery,
			fetchPolicy: 'network-only',
		},
	);

	const editDeniedButCanView = skipViewQuery
		? false
		: Boolean(data?.singleContent) && data?.singleContent?.status !== 'draft';
	const viewLink = editDeniedButCanView
		? `${CONTEXT_PATH}${data?.singleContent?.links?.webui}`
		: null;
	const canBeSuperAdmin = Boolean(data?.user?.confluence?.roles?.canBeSuperAdmin);
	// Only redirect user if:
	// 1. They have a viewLink available.
	// 2. They can't be super admin.
	const shouldRedirectToView = spaceKey && viewLink && !canBeSuperAdmin;

	useEffect(() => {
		if (shouldRedirectToView) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirected',
					actionSubject: 'redirectToViewMode',
					source: 'restrictedPageScreen',
					attributes: {
						accessType,
						contentId,
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: RESTRICTED_PAGE_EXPERIENCE,
			});

			replace(
				VIEW_PAGE.toUrl(
					{
						spaceKey,
						contentId,
					},
					{
						query: {
							redirectedFromRestrict: 'true',
						},
					},
				),
			);
		}
	}, [
		createAnalyticsEvent,
		contentId,
		shouldRedirectToView,
		accessType,
		spaceKey,
		replace,
		experienceTracker,
	]);

	if (loading) {
		return <ShowSpinnerAfterDelay />;
	} else {
		/**
		 * Usually, if the user doesn't have permission to view the page, the
		 * query succeeds with `singleContent: null`. But occasionally we get a
		 * 401 or 403, and we'd consider those to be expected errors.
		 */
		let hasUnhandledError = false;
		if (error) {
			if (is401(error)) markErrorAsHandled(error);
			else if (is403(error)) markErrorAsHandled(error);
			else hasUnhandledError = true;
		}

		return shouldRedirectToView ? (
			<ShowSpinnerAfterDelay />
		) : (
			<>
				{hasUnhandledError && (
					<>
						<ExperienceFailure name={RESTRICTED_PAGE_EXPERIENCE} error={error!} />
						<ErrorDisplay error={error!} />
					</>
				)}
				<RestrictedPageComponent
					{...props}
					editDeniedButCanView={editDeniedButCanView}
					viewLink={viewLink}
				/>
			</>
		);
	}
};

export const RestrictedPage = (props: RestrictedPageProps) => (
	<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
		<ExperienceStart name={RESTRICTED_PAGE_EXPERIENCE} />
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => <RestrictedPageWithCanViewQuery {...props} flags={flags} />}
		</Subscribe>
	</ErrorBoundary>
);
